import { supabase } from '@/integrations/supabase/client';
import { Inversor, CreateInversor, UpdateInversor, InversorArquiteturaRede } from '@/types/inversores';
import { resolveAllowedImageMimeType } from '@/utils/imageMime';

function mapArquiteturaRede(value: string | null | undefined): InversorArquiteturaRede {
  return value === 'hibrido' ? 'hibrido' : 'ongrid';
}

// Tipos para dados do banco
interface InversorDB {
  id: string;
  marca: string;
  modelo: string;
  potencia_nominal_w?: number;
  potencia_nominal_kw?: number;
  potencia_maxima_entrada: number;
  tensao_maxima_vcc: number;
  tensao_partida_vcc: number;
  faixa_tensao_mppt_minima: number;
  faixa_tensao_mppt_maxima: number;
  tensao_nominal_vcc: number;
  corrente_maxima_mppt: number;
  mppt_independentes: number;
  strings_por_mppt: number;
  potencia_aparente_maxima: number;
  corrente_maxima_saida: number;
  tensao_operacao_ca: number;
  frequencia: number;
  fator_potencia: number;
  tipo_conexao: string;
  garantia: number;
  microinversor?: boolean;
  arquitetura_rede?: string;
  imagem_url?: string | null;
  created_at: string;
  updated_at: string;
}

export const inversoresService = {
  // Upload de imagem do inversor
  async uploadImagem(inversorId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${inversorId}_${Date.now()}.${fileExt}`;
    const filePath = `inversores/${fileName}`;

    const contentType = resolveAllowedImageMimeType(file);
    if (!contentType) {
      throw new Error('Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.');
    }

    // Upload para Supabase Storage
    const { data, error } = await supabase.storage
      .from('inversores')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType
      });

    if (error) {
      console.error('Erro no upload da imagem:', error);
      throw new Error(`Erro ao fazer upload da imagem: ${error.message}`);
    }

    // Obter URL pública
    const { data: urlData } = supabase.storage
      .from('inversores')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  },

  // Buscar todos os inversores
  async getAll(): Promise<Inversor[]> {
    const { data, error } = await supabase
      .from('inversores')
      .select('*')
      .order('marca', { ascending: true });

    if (error) {
      console.error('Erro detalhado ao buscar inversores:', error);
      throw new Error(`Erro ao buscar inversores: ${error.message || error.details || 'Erro desconhecido'}`);
    }

    // Transformar dados do banco para o formato da interface
    return (data || []).map((item: InversorDB) => ({
      id: item.id,
      marca: item.marca,
      modelo: item.modelo,
      potenciaNominal: item.potencia_nominal_w || (item.potencia_nominal_kw * 1000),
      potenciaMaxima: item.potencia_maxima_entrada,
      tensaoMaxima: item.tensao_maxima_vcc,
      tensaoPartida: item.tensao_partida_vcc,
      faixaTensaoMPPT: {
        minima: item.faixa_tensao_mppt_minima,
        maxima: item.faixa_tensao_mppt_maxima
      },
      tensaoNominal: item.tensao_nominal_vcc,
      correnteMaximaMPPT: item.corrente_maxima_mppt,
      mpptIndependentes: item.mppt_independentes,
      stringsPorMPPT: item.strings_por_mppt,
      potenciaAparenteMaxima: item.potencia_aparente_maxima,
      correnteMaximaSaida: item.corrente_maxima_saida,
      tensaoOperacao: item.tensao_operacao_ca || 220,
      frequencia: item.frequencia,
      fatorPotencia: item.fator_potencia,
      tipoConexao: item.tipo_conexao,
      garantia: item.garantia,
      microinversor: item.microinversor ?? false,
      arquiteturaRede: mapArquiteturaRede(item.arquitetura_rede),
      imagemUrl: item.imagem_url || null,
      createdAt: item.created_at,
      updatedAt: item.updated_at
    }));
  },

  // Buscar inversor por ID
  async getById(id: string): Promise<Inversor | null> {
    const { data, error } = await supabase
      .from('inversores')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erro ao buscar inversor:', error);
      return null;
    }

    if (!data) return null;

    // Transformar dados do banco para o formato da interface
    return {
      id: data.id,
      marca: data.marca,
      modelo: data.modelo,
      potenciaNominal: data.potencia_nominal_w || (data.potencia_nominal_kw * 1000),
      potenciaMaxima: data.potencia_maxima_entrada,
      tensaoMaxima: data.tensao_maxima_vcc,
      tensaoPartida: data.tensao_partida_vcc,
      faixaTensaoMPPT: {
        minima: data.faixa_tensao_mppt_minima,
        maxima: data.faixa_tensao_mppt_maxima
      },
      tensaoNominal: data.tensao_nominal_vcc,
      correnteMaximaMPPT: data.corrente_maxima_mppt,
      mpptIndependentes: data.mppt_independentes,
      stringsPorMPPT: data.strings_por_mppt,
      potenciaAparenteMaxima: data.potencia_aparente_maxima,
      correnteMaximaSaida: data.corrente_maxima_saida,
      tensaoOperacao: data.tensao_operacao_ca || 220,
      frequencia: data.frequencia,
      fatorPotencia: data.fator_potencia,
      tipoConexao: data.tipo_conexao,
      garantia: data.garantia,
      microinversor: data.microinversor ?? false,
      arquiteturaRede: mapArquiteturaRede(data.arquitetura_rede),
      imagemUrl: data.imagem_url || null,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  // Criar novo inversor
  async create(inversor: CreateInversor): Promise<Inversor> {
    console.log('Dados recebidos para criação:', inversor);
    
    // Validar dados antes de enviar
    if (!inversor.marca || !inversor.modelo) {
      throw new Error('Marca e modelo são obrigatórios');
    }
    
    const insertData: any = {
      marca: inversor.marca,
      modelo: inversor.modelo,
      potencia_nominal_kw: Number((inversor.potenciaNominal / 1000).toFixed(2)),
      potencia_nominal_w: inversor.potenciaNominal,
      potencia_maxima_entrada: inversor.potenciaMaxima,
      tensao_maxima_vcc: inversor.tensaoMaxima,
      tensao_partida_vcc: inversor.tensaoPartida,
      faixa_tensao_mppt_minima: inversor.faixaTensaoMPPT.minima,
      faixa_tensao_mppt_maxima: inversor.faixaTensaoMPPT.maxima,
      tensao_nominal_vcc: inversor.tensaoNominal,
      corrente_maxima_mppt: inversor.correnteMaximaMPPT,
      mppt_independentes: inversor.mpptIndependentes,
      strings_por_mppt: inversor.stringsPorMPPT,
      potencia_aparente_maxima: inversor.potenciaAparenteMaxima,
      corrente_maxima_saida: inversor.correnteMaximaSaida,
      tensao_operacao_ca: inversor.tensaoOperacao,
      frequencia: inversor.frequencia,
      fator_potencia: inversor.fatorPotencia,
      tipo_conexao: inversor.tipoConexao,
      garantia: inversor.garantia,
      microinversor: inversor.microinversor ?? false,
      arquitetura_rede: inversor.arquiteturaRede ?? 'ongrid'
    };

    if (inversor.imagemUrl) {
      insertData.imagem_url = inversor.imagemUrl;
    }
    
    console.log('Dados para inserção:', insertData);
    
    const { data, error } = await supabase
      .from('inversores')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Erro detalhado ao criar inversor:', error);
      throw new Error(`Erro ao criar inversor: ${error.message || error.details || 'Erro desconhecido'}`);
    }

    console.log('Inversor criado com sucesso:', data);

    // Transformar dados do banco para o formato da interface
    return {
      id: data.id,
      marca: data.marca,
      modelo: data.modelo,
      potenciaNominal: data.potencia_nominal_w || (data.potencia_nominal_kw * 1000),
      potenciaMaxima: data.potencia_maxima_entrada,
      tensaoMaxima: data.tensao_maxima_vcc,
      tensaoPartida: data.tensao_partida_vcc,
      faixaTensaoMPPT: {
        minima: data.faixa_tensao_mppt_minima,
        maxima: data.faixa_tensao_mppt_maxima
      },
      tensaoNominal: data.tensao_nominal_vcc,
      correnteMaximaMPPT: data.corrente_maxima_mppt,
      mpptIndependentes: data.mppt_independentes,
      stringsPorMPPT: data.strings_por_mppt,
      potenciaAparenteMaxima: data.potencia_aparente_maxima,
      correnteMaximaSaida: data.corrente_maxima_saida,
      tensaoOperacao: data.tensao_operacao_ca || 220,
      frequencia: data.frequencia,
      fatorPotencia: data.fator_potencia,
      tipoConexao: data.tipo_conexao,
      garantia: data.garantia,
      microinversor: data.microinversor ?? false,
      arquiteturaRede: mapArquiteturaRede(data.arquitetura_rede),
      imagemUrl: data.imagem_url || null,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  // Atualizar inversor existente
  async update(id: string, inversor: UpdateInversor): Promise<Inversor> {
    const updateData: any = {};
    
    if (inversor.marca !== undefined) updateData.marca = inversor.marca;
    if (inversor.modelo !== undefined) updateData.modelo = inversor.modelo;
    if (inversor.potenciaNominal !== undefined) {
      updateData.potencia_nominal_kw = Number((inversor.potenciaNominal / 1000).toFixed(2));
      updateData.potencia_nominal_w = inversor.potenciaNominal;
    }
    if (inversor.potenciaMaxima !== undefined) updateData.potencia_maxima_entrada = inversor.potenciaMaxima;
    if (inversor.tensaoMaxima !== undefined) updateData.tensao_maxima_vcc = inversor.tensaoMaxima;
    if (inversor.tensaoPartida !== undefined) updateData.tensao_partida_vcc = inversor.tensaoPartida;
    if (inversor.faixaTensaoMPPT?.minima !== undefined) updateData.faixa_tensao_mppt_minima = inversor.faixaTensaoMPPT.minima;
    if (inversor.faixaTensaoMPPT?.maxima !== undefined) updateData.faixa_tensao_mppt_maxima = inversor.faixaTensaoMPPT.maxima;
    if (inversor.tensaoNominal !== undefined) updateData.tensao_nominal_vcc = inversor.tensaoNominal;
    if (inversor.correnteMaximaMPPT !== undefined) updateData.corrente_maxima_mppt = inversor.correnteMaximaMPPT;
    if (inversor.mpptIndependentes !== undefined) updateData.mppt_independentes = inversor.mpptIndependentes;
    if (inversor.stringsPorMPPT !== undefined) updateData.strings_por_mppt = inversor.stringsPorMPPT;
    if (inversor.potenciaAparenteMaxima !== undefined) updateData.potencia_aparente_maxima = inversor.potenciaAparenteMaxima;
    if (inversor.correnteMaximaSaida !== undefined) updateData.corrente_maxima_saida = inversor.correnteMaximaSaida;
    if (inversor.tensaoOperacao !== undefined) updateData.tensao_operacao_ca = inversor.tensaoOperacao;
    if (inversor.frequencia !== undefined) updateData.frequencia = inversor.frequencia;
    if (inversor.fatorPotencia !== undefined) updateData.fator_potencia = inversor.fatorPotencia;
    if (inversor.tipoConexao !== undefined) updateData.tipo_conexao = inversor.tipoConexao;
    if (inversor.garantia !== undefined) updateData.garantia = inversor.garantia;
    if (inversor.microinversor !== undefined) updateData.microinversor = inversor.microinversor;
    if (inversor.arquiteturaRede !== undefined) updateData.arquitetura_rede = inversor.arquiteturaRede;
    if (inversor.imagemUrl !== undefined) updateData.imagem_url = inversor.imagemUrl;

    const { data, error } = await supabase
      .from('inversores')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro detalhado ao atualizar inversor:', error);
      throw new Error(`Erro ao atualizar inversor: ${error.message || error.details || 'Erro desconhecido'}`);
    }

    // Transformar dados do banco para o formato da interface
    return {
      id: data.id,
      marca: data.marca,
      modelo: data.modelo,
      potenciaNominal: data.potencia_nominal_w || (data.potencia_nominal_kw * 1000),
      potenciaMaxima: data.potencia_maxima_entrada,
      tensaoMaxima: data.tensao_maxima_vcc,
      tensaoPartida: data.tensao_partida_vcc,
      faixaTensaoMPPT: {
        minima: data.faixa_tensao_mppt_minima,
        maxima: data.faixa_tensao_mppt_maxima
      },
      tensaoNominal: data.tensao_nominal_vcc,
      correnteMaximaMPPT: data.corrente_maxima_mppt,
      mpptIndependentes: data.mppt_independentes,
      stringsPorMPPT: data.strings_por_mppt,
      potenciaAparenteMaxima: data.potencia_aparente_maxima,
      correnteMaximaSaida: data.corrente_maxima_saida,
      tensaoOperacao: data.tensao_operacao_ca || 220,
      frequencia: data.frequencia,
      fatorPotencia: data.fator_potencia,
      tipoConexao: data.tipo_conexao,
      garantia: data.garantia,
      microinversor: data.microinversor ?? false,
      arquiteturaRede: mapArquiteturaRede(data.arquitetura_rede),
      imagemUrl: data.imagem_url || null,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  },

  // Excluir inversor
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('inversores')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro detalhado ao excluir inversor:', error);
      throw new Error(`Erro ao excluir inversor: ${error.message || error.details || 'Erro desconhecido'}`);
    }
  },

  // Buscar inversores por filtros
  async search(query: string): Promise<Inversor[]> {
    const { data, error } = await supabase
      .from('inversores')
      .select('*')
      .or(`marca.ilike.%${query}%,modelo.ilike.%${query}%,tipo_conexao.ilike.%${query}%`)
      .order('marca', { ascending: true });

    if (error) {
      console.error('Erro detalhado ao buscar inversores:', error);
      throw new Error(`Erro ao buscar inversores: ${error.message || error.details || 'Erro desconhecido'}`);
    }

    // Transformar dados do banco para o formato da interface
    return (data || []).map((item: any) => ({
      id: item.id,
      marca: item.marca,
      modelo: item.modelo,
      potenciaNominal: item.potencia_nominal_w || (item.potencia_nominal_kw * 1000),
      potenciaMaxima: item.potencia_maxima_entrada,
      tensaoMaxima: item.tensao_maxima_vcc,
      tensaoPartida: item.tensao_partida_vcc,
      faixaTensaoMPPT: {
        minima: item.faixa_tensao_mppt_minima,
        maxima: item.faixa_tensao_mppt_maxima
      },
      tensaoNominal: item.tensao_nominal_vcc,
      correnteMaximaMPPT: item.corrente_maxima_mppt,
      mpptIndependentes: item.mppt_independentes,
      stringsPorMPPT: item.strings_por_mppt,
      potenciaAparenteMaxima: item.potencia_aparente_maxima,
      correnteMaximaSaida: item.corrente_maxima_saida,
      tensaoOperacao: item.tensao_operacao_ca || 220,
      frequencia: item.frequencia,
      fatorPotencia: item.fator_potencia,
      tipoConexao: item.tipo_conexao,
      garantia: item.garantia,
      microinversor: item.microinversor ?? false,
      arquiteturaRede: mapArquiteturaRede(item.arquitetura_rede),
      imagemUrl: item.imagem_url || null,
      createdAt: item.created_at,
      updatedAt: item.updated_at
    }));
  }
};
